import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { createRef, useEffect, useState } from 'react'
import { MdAdd } from 'react-icons/md'
import { Link, useParams } from 'react-router-dom'

import type { RefObject, ReactElement, SyntheticEvent } from 'react'
import type { IGlossaryEntry } from '~/types/glossary'

import EntryWrapper from './EntryWrapper'
import ManageEntryModal from './ManageEntryModal'

import Button from '~/components/Buttons/Button'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import SearchBarLayout from '~/components/SearchBarLayout'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS, alphabet } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { keys as glossaryKeys, useDeleteGlossaryEntryMutation, useGlossaryListQuery } from '~/queries/glossary'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const Glossary = (): ReactElement => {
  /** Local state */

  const { searchParamsObject } = useSearchParamsContext()

  const params = useParams<{ id: string }>()

  const queryClient = useQueryClient()

  const [glossaryData, setGlossaryData] = useState<IGlossaryEntry[]>([])

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [isManageModalOpen, setIsManageModalOpen] = useState(false)

  const [query, setQuery] = useState(searchParamsObject.search || '')

  const [refElements, setRefElements] = useState<{ [key: string]: RefObject<HTMLDivElement> }>()

  const [selectedChar, setSelectedChar] = useState<string>('')

  const [selectedEntry, setSelectedEntry] = useState<IGlossaryEntry>()

  const isUserAuthorizedToWrite = useIsUserAuthorized([AUTHORIZATIONS.VERSION.GLOSSARY.WRITE])

  const firstChars: string[] = []

  /** Queries & Mutations */

  const { data, isLoading } = useGlossaryListQuery()

  const { mutateAsync: deleteGlossaryEntry } = useDeleteGlossaryEntryMutation()

  /** Effects */

  useDocumentTitle('Glossary')

  useEffect(() => {
    if (data) {
      if (query === '') {
        setGlossaryData(data || [])
      }

      const regex = new RegExp(query, 'ui')

      setGlossaryData(data?.filter(({ title }) => regex.test(title)) || [])
    }
  }, [query, data])

  useEffect(() => {
    if (selectedChar) document.getElementById(`section-${selectedChar}`)?.scrollIntoView({ behavior: 'smooth' })
  }, [selectedChar])

  // HANDLING OF SCROLL TO ELEMENT FROM ID PARAM
  useEffect(() => {
    const refs: { [key: string]: RefObject<HTMLDivElement> } = {}

    if (glossaryData) {
      glossaryData.forEach(({ glossary_item_id }) => {
        refs[glossary_item_id] = createRef()
      })
    }

    setRefElements(refs)
  }, [glossaryData, setRefElements])

  useEffect(() => {
    if (refElements && params?.id && refElements[params.id]) {
      const element = refElements[params.id]?.current

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' })
        }, 500)
      }
    }
  }, [params.id, refElements])

  /** Handlers */

  const onQueryChange = (evt: SyntheticEvent<HTMLInputElement>) => setQuery(evt.currentTarget.value)

  const handleManageModalState = (isOpen: boolean, entry?: IGlossaryEntry) => () => {
    setSelectedEntry(entry)
    setIsManageModalOpen(isOpen)
  }

  const handleManageEntrySuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: glossaryKeys.list() })
    displayToast(`Entry successfully ${selectedEntry ? 'edited' : 'created'}`, 'success')
    setIsManageModalOpen(false)
  }

  const handleManageEntryFailure = () => {
    displayToast(`Entry could not be ${selectedEntry ? 'edited' : 'created'}`, 'error')
    setIsManageModalOpen(false)
  }

  const handleDeleteModalState = (isOpen: boolean, entry?: IGlossaryEntry) => () => {
    setSelectedEntry(entry)
    setIsDeleteModalOpen(isOpen)
  }

  const handleDeleteConfirm = async () => {
    if (selectedEntry) {
      try {
        await deleteGlossaryEntry(selectedEntry.glossary_item_id)
        await queryClient.invalidateQueries({ queryKey: glossaryKeys.list() })
        setSelectedEntry(undefined)
        setIsDeleteModalOpen(false)
        displayToast('Entry successfully deleted', 'success')
      } catch {
        displayToast('Entry could not be removed', 'error')
      }
    }
  }

  /** Render */

  return (
    <>
      <ViewTitle className="layout-view-title">Glossary</ViewTitle>
      <SearchBarLayout>
        <div className="search-container">
          <div className="search-bar-wrapper">
            <SearchInput
              className="table-search-bar"
              disabled={isLoading}
              id="glossary-search-bar"
              onChange={onQueryChange}
              value={query}
            />
          </div>
          {isUserAuthorizedToWrite ? (
            <Button className="add-entry" disabled={isLoading} onClick={handleManageModalState(true)}>
              <MdAdd color="#fff" />
              New word
            </Button>
          ) : null}
        </div>
      </SearchBarLayout>
      <Layout className="glossary-view" withPadding>
        <div className="glossary-content">
          {isLoading ? (
            <MwMLoader category="item" />
          ) : (
            <div className="entries">
              {refElements
                ? glossaryData.map((el) => {
                    let withFirstChar = false
                    const firstChar = el.title[0].toUpperCase()

                    if (!firstChars.includes(firstChar)) {
                      withFirstChar = true
                      firstChars.push(firstChar)
                    }

                    return (
                      <div key={el.glossary_item_id} ref={refElements[el.glossary_item_id]}>
                        <EntryWrapper
                          entry={el}
                          handleDeleteModalState={handleDeleteModalState}
                          handleManageModalState={handleManageModalState}
                          isUserAuthorizedToWrite={isUserAuthorizedToWrite}
                          withFirstChar={withFirstChar}
                        />
                      </div>
                    )
                  })
                : null}
            </div>
          )}
        </div>
        <div className="nav-links">
          {alphabet.map((char) => {
            const isDisabled = !firstChars.includes(char)
            const isSelected = selectedChar === char
            const classes = classNames({ disabled: isDisabled, selected: isSelected })

            return (
              <Link className={classes} key={char} onClick={() => !isDisabled && setSelectedChar(char)} to="#">
                {char}
              </Link>
            )
          })}
        </div>
      </Layout>
      <ManageEntryModal
        entry={selectedEntry}
        isOpen={isManageModalOpen}
        onClose={handleManageModalState(false)}
        onFailure={handleManageEntryFailure}
        onSuccess={handleManageEntrySuccess}
      />
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Delete glossary entry"
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteConfirm}
        subText="Deleting a glossary entry will permanently remove it from the database."
      />
    </>
  )
}

export default Glossary
